import { useTranslation } from "react-i18next";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useRoleAccess } from "../hooks/useRoleAccess";
import { OrdersSection } from "../hooks/settingsPanels";
import OrdersPanel from "./OrdersPanel";
import BillingPanel from "./BillingPanel";
import StatisticsPanel from "./StatisticsPanel";

/**
 * „Bestellungen" mit Abrechnung und Statistiken als Reitern — beide stehen nicht mehr als eigene
 * Einträge in der Seitenleiste.
 *
 * Jeder Reiter bleibt hinter seiner bisherigen Rolle. Das verliert niemanden: Wer Abrechnung oder
 * Statistiken sehen darf, darf auch die Bestellungen sehen (`canSeeOrders` umfasst beide).
 *
 * Der Reiter steht in `?section=`, nicht in `?tab=` — `tab` gehört den Reitern der Statistiken.
 * Radix hängt nur den aktiven Reiter ein, Abrechnung und Statistiken laden also erst beim Öffnen.
 */
export default function OrdersSections({
    section,
    onSectionChange,
}: {
    section: string | null;
    onSectionChange: (section: OrdersSection) => void;
}) {
    const { t } = useTranslation();
    const roleAccess = useRoleAccess();

    const sections: OrdersSection[] = [
        "orders",
        ...(roleAccess.canSeeBilling ? (["billing"] as const) : []),
        ...(roleAccess.canSeeStatistics ? (["statistics"] as const) : []),
    ];
    const active = sections.find((candidate) => candidate === section) ?? "orders";

    return (
        <Tabs
            value={active}
            onValueChange={(value) => onSectionChange(value as OrdersSection)}
            className="min-w-0"
        >
            <TabsList className="max-w-full overflow-x-auto">
                {sections.map((candidate) => (
                    <TabsTrigger key={candidate} value={candidate}>
                        {t(`settingsPanel.${candidate}`)}
                    </TabsTrigger>
                ))}
            </TabsList>
            <TabsContent value="orders">
                <OrdersPanel />
            </TabsContent>
            {roleAccess.canSeeBilling && (
                <TabsContent value="billing">
                    <BillingPanel />
                </TabsContent>
            )}
            {roleAccess.canSeeStatistics && (
                <TabsContent value="statistics">
                    <StatisticsPanel />
                </TabsContent>
            )}
        </Tabs>
    );
}
